//Replace widget-dashboard-subtitle.ts
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import * as _ from "lodash";

import { SWReactIcons } from "@similarweb/icons";
import type { IconName } from "@similarweb/icons";
import { PopupHoverContainer } from "@similarweb/ui-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { getDomainWithSubdomain, getUrlComponents } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import type { ILocationObject } from "app/components/campaigns/queryBar-report/reports-pages-filters/CmapaignsIfraTypes";
import { BooleanSearchWidgetSubtitleItem } from "app/components/dashboard/BooleanSearchWidgetSubtitleItem";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import { getRelevantDevicesList } from "app/components/dashboard/widget-wizard/utils/dashboardWizardUtils";
import WidgetCustomAssetTooltip from "app/components/dashboard/WidgetCutomAssetTooltip";
import { SWReactCountryIconsInline } from "app/components/React/SWReactIcons/SWReactCountryIconsInline";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import { Widget } from "app/components/widget/widget-types/Widget";
import { SEGMENTS_FAMILY_NAME } from "app/components/widget/widget-utilities/segments/constants";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { getCustomAssetTooltipInfo } from "app/dashboards/view-components/utils";
import { WidgetLegendContainerV2 } from "app/dashboards/view-components/widget-legend/WidgetLegendContainer-v2";
import { industryKeyToIconFilter, industryKeyToTitleFilter } from "app/filters/ngFilters";
import type { EDevice, IDeviceFilterItem } from "app/pages/app-intelligence/common-components/device-picker/types";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";

import { CalendarIconWrapper, StyledSubTitle, StyledTitle, StyledTitleWrapper } from "./styledComponents";

interface ISubsData {
    family: string;
    customSubtitle: string;
}

const NoSubtitleCmp = ({ widget }) => {
    const { type } = widget;

    return <h3 className={"noSubtitleCmp"}>No subtitle for {type}</h3>;
};

export const WidgetDashboardSubtitleV2: FC<IDashboardWidgetInterface> = (props) => {
    const { widget } = props;
    const t = useTranslation();

    const [durationStr, setDurationStr] = useState("");

    const isSingleMode = widget.getWidgetModel().key.length == 1;
    const firstLegendItem = { ...widget.viewData.key[0] } as any;
    const { pageFilter } = widget._params;
    if (pageFilter) {
        const pageFilterParsed = JSON.parse(pageFilter);
        const selectedPageFilter = pageFilterParsed.find((item) => {
            const { domain, subdomain } = getUrlComponents(item.url);
            const currentDomainKey = getDomainWithSubdomain(domain, subdomain);
            return item.url === firstLegendItem.name || currentDomainKey === firstLegendItem.name;
        });

        if (selectedPageFilter) {
            firstLegendItem.name = selectedPageFilter.url;
            firstLegendItem.displayName = selectedPageFilter.url;
            firstLegendItem.reverseEllipsis =
                selectedPageFilter.searchType === "exact" || selectedPageFilter.searchType === "folder";
        }
    }
    const item = useMemo(() => {
        return firstLegendItem && (firstLegendItem.Title || firstLegendItem.name || firstLegendItem.domain)
            ? firstLegendItem
            : { name: _.head<any>(widget.apiParams.keys.split(",")) };
    }, [widget]);

    const subsData: ISubsData = {
        family: widget._widgetConfig.properties?.family,
        customSubtitle: t(widget.viewData.customSubtitle),
    };

    let industryIcon = "";
    if (subsData.family === "Industry") {
        if (item && item.category && item.category.indexOf("$") > -1) {
            industryIcon = industryKeyToIconFilter()(item.category);
        } else {
            industryIcon = industryKeyToIconFilter()(`*${item.name}`);
        }
    }

    const getFilterValue = useMemo(() => {
        const filterObject: any = Widget.filterParse(widget.apiParams.filter);
        const filterObjectItem: any = filterObject[widget.getSearchKey()];
        return filterObjectItem && filterObjectItem.hasOwnProperty("value")
            ? filterObjectItem.value.replace(/"/g, "")
            : "";
    }, [widget.apiParams.filter]);

    useEffect(() => {
        if (_.isArray(widget.durationObject.forWidget)) {
            setDurationStr(widget.durationObject.forWidget.join(` ${t("compared.duration.vs")} `));
        } else {
            setDurationStr(widget.durationObject.forWidget);
        }
    }, [widget]);

    const customAssetIcon = useMemo(() => {
        if (item.category && item.category.indexOf("$") === -1) {
            return industryIcon;
        }
        if (subsData.family === "Keyword" && item.id && item.id !== item.name) {
            return "icon sw-icon-folder";
        }
        return null;
    }, [item]);

    const useLegendInSubtitle = isSingleMode && widget.apiParams.compareFrom && widget.periodOverPeriodLegend;

    const [customAssetTooltip, setCustomAssetTooltip] = useState({
        title: "",
        type: "",
        items: [],
    });
    const [singleTitle, setSingleTitle] = useState("");

    let deviceObject: IDeviceFilterItem;
    if (widget.apiParams.store && widget.apiParams.device) {
        const { store, device: deviceParam } = widget.apiParams;
        const deviceList: IDeviceFilterItem[] = getRelevantDevicesList(
            store,
            widget.legendItems?.map((legendItem) => legendItem.id),
            widget._metricConfig?.state,
            deviceParam as EDevice,
        );
        deviceObject = deviceList?.find(
            (device) => device.id?.toLowerCase() === (deviceParam?.toLowerCase() as EDevice),
        );
    }

    useEffect(() => {
        setSingleTitle(item.name);
        let tmpSingleTitle = null;
        if (customAssetIcon) {
            tmpSingleTitle = item.name;
            switch (subsData.family) {
                case "Industry": {
                    setCustomAssetTooltip((prev) => {
                        return {
                            ...prev,
                            title: "Websites in category:",
                            type: "websites",
                        };
                    });
                    const customAssetTooltipInfo = getCustomAssetTooltipInfo(item.id, item.name);
                    setCustomAssetTooltip((prev) => {
                        return {
                            ...prev,
                            items: customAssetTooltipInfo.items,
                        };
                    });
                    setSingleTitle(customAssetTooltipInfo.tmpSingleTitle);
                    break;
                }
                case "Keyword": {
                    setCustomAssetTooltip((prev) => {
                        return {
                            ...prev,
                            title: "Keywords in group:",
                            type: "keywords",
                        };
                    });
                    keywordGroupsQueryClient.invalidateAll().then(() => {
                        keywordGroupsQueryClient.getGroupById(item.id).then(
                            (
                                keywordsGroup: IUserKeywordGroup & {
                                    keywords?: string[];
                                    name?: string;
                                },
                            ) => {
                                tmpSingleTitle = keywordsGroup ? keywordsGroup.name || keywordsGroup.Name : item.name;

                                setCustomAssetTooltip((prev) => {
                                    return {
                                        ...prev,
                                        items: keywordsGroup ? keywordsGroup.keywords || keywordsGroup.Keywords : [],
                                    };
                                });
                                setSingleTitle(tmpSingleTitle);
                            },
                        );
                    });
                    break;
                }
            }
        }
    }, [customAssetIcon]);

    const itemIcon = useMemo(() => {
        return item.icon || item.Icon || item.image || item.favicon;
    }, [item]);

    const CustomSubtitle = widget.viewData.customSubtitleComponent ?? NoSubtitleCmp;

    const isSegments = subsData.family === SEGMENTS_FAMILY_NAME;
    const isSegmentsGeography = widget.type === "SegmentsGeography";
    const isIconVisible =
        isSegmentsGeography || (!isSegments && isSingleMode) || subsData.family === EFamilyTypes.RankTracker;

    return (
        <div
            style={{
                display: "block",
                marginTop: -25,
                minHeight: 15,
                marginBottom: widget.viewOptions.dashboardSubtitleMarginBottom,
            }}
            className={widget.viewOptions.dashboardSubtitleClass}
        >
            <ul className="swWidget-dashboard-subTitle u-flex-row">
                {isIconVisible ? (
                    <li className="swWidget-dashboard-subTitle-item-keyword">
                        {!customAssetIcon ? (
                            <span
                                className={classNames(`item-img ${industryIcon}`, {
                                    "sw-icon-keyword": subsData.family === "Keyword" && !customAssetIcon,
                                })}
                                title={item.name || item.Title}
                            >
                                {itemIcon && subsData.family !== "Industry" && subsData.family !== "Keyword" ? (
                                    <img
                                        className={classNames("item-icon", {
                                            "website-small-icon": item.smallIcon,
                                        })}
                                        crossOrigin="anonymous"
                                        src={itemIcon}
                                        alt={"anonymous"}
                                    />
                                ) : null}
                            </span>
                        ) : null}
                        {!customAssetIcon ? (
                            <StyledTitleWrapper>
                                {isSegmentsGeography ? (
                                    <>
                                        <StyledSubTitle title={item?.domain}>
                                            {industryKeyToTitleFilter()(item?.domain)}
                                        </StyledSubTitle>
                                        <StyledTitle title={item.name || item.Title}>
                                            {industryKeyToTitleFilter()(
                                                item.category || item.name || item.Title || item?.segmentName,
                                            )}
                                        </StyledTitle>
                                    </>
                                ) : (
                                    <span
                                        className={classNames("item-name", {
                                            "item-reverse-ellipsis": item.reverseEllipsis,
                                        })}
                                        title={item.name || item.Title}
                                    >
                                        {industryKeyToTitleFilter()(item.category || item.name || item.Title)}
                                    </span>
                                )}
                            </StyledTitleWrapper>
                        ) : null}

                        {customAssetIcon ? (
                            <WidgetCustomAssetTooltip
                                iconClasName={customAssetIcon}
                                title={customAssetTooltip.title}
                                listItems={customAssetTooltip.items}
                                type={customAssetTooltip.type}
                                visibleItems={7}
                                name={singleTitle}
                            />
                        ) : null}
                    </li>
                ) : null}
                {subsData.family === "Mobile" && !isSingleMode && widget.apiParams.metric === "AppIndexes" ? (
                    <li className="swWidget-dashboard-subTitle-item-legend">
                        <span className="swWidget-dashboard-subTitle-country">{t("grid.filter.category.all")}</span>
                    </li>
                ) : null}

                {useLegendInSubtitle ? (
                    <li className="swWidget-dashboard-subTitle-item-legend">
                        <WidgetLegendContainerV2
                            widget={widget}
                            className="swWidget-legend"
                            items={widget.periodOverPeriodLegendItems}
                            customTemplate={widget.customLegendTemplate}
                        />
                    </li>
                ) : null}

                {subsData.family === EFamilyTypes.RankTracker ||
                !widget.viewOptions.hideDurationFromSubtitle ||
                (!useLegendInSubtitle && !widget.viewOptions.hideDurationFromSubtitle && !subsData.customSubtitle) ? (
                    <li className="swWidget-dashboard-subTitle-item-duration">
                        {subsData.family === "Segments" ? (
                            <CalendarIconWrapper>
                                <SWReactIcons iconName="daily-ranking" />
                            </CalendarIconWrapper>
                        ) : null}
                        <span>{widget.viewData.duration || durationStr}</span>
                    </li>
                ) : null}

                {subsData.customSubtitle ? (
                    <li>
                        <span
                            className="item-name"
                            title={subsData.customSubtitle}
                        >
                            {widget.viewData.customSubtitleComponent ? <CustomSubtitle widget={widget} /> : null}
                            {!widget.viewData.customSubtitleComponent ? <>{subsData.customSubtitle}</> : null}
                        </span>
                    </li>
                ) : null}
                {!widget.viewOptions?.hasCountryLegend ? (
                    <li className="swWidget-dashboard-subTitle-item-country">
                        <PopupHoverContainer
                            content={() => (
                                <FlexColumn>
                                    {widget.viewData.country?.selectedLocations?.map((location: ILocationObject) => (
                                        <div key={location.text}>{location.text}</div>
                                    ))}
                                </FlexColumn>
                            )}
                            config={{
                                enabled:
                                    subsData.family === EFamilyTypes.RankTracker &&
                                    widget.viewData.country?.selectedLocations?.length > 1,
                                placement: "bottom",
                                allowHover: true,
                                closeDelay: 500,
                                cssClass: "Popup-element-wrapper--lite",
                                cssClassContent: "Popup-content--lite",
                            }}
                        >
                            <div>
                                <SWReactCountryIconsInline countryCode={widget.viewData.country?.id} />
                                <span
                                    title={widget.viewData.country?.text}
                                    className="swWidget-dashboard-subTitle-country"
                                >
                                    {widget.viewData.country?.text}
                                </span>
                            </div>
                        </PopupHoverContainer>
                    </li>
                ) : null}

                {getFilterValue !== "" ? (
                    <li className="swWidget-dashboard-subTitle-item-filter">
                        <SWReactIcons iconName={"search"} />
                        <span className="swWidget-dashboard-subTitle-filter">{getFilterValue}</span>
                    </li>
                ) : null}

                {deviceObject ? (
                    <li className="swWidget-dashboard-subTitle-item-device">
                        <SWReactIcons iconName={deviceObject?.icon as IconName} />
                        <span className="swWidget-dashboard-subTitle-device">{deviceObject?.text}</span>
                    </li>
                ) : null}

                {widget.viewData.subtitleTextItem?.text ? (
                    <li className="swWidget-dashboard-subTitle-item-text">
                        <PopupHoverContainer
                            content={() => <FlexColumn>{widget.viewData.subtitleTextItem?.tooltipValue}</FlexColumn>}
                            config={{
                                enabled: !!widget.viewData.subtitleTextItem?.tooltipValue,
                                placement: "bottom",
                                allowHover: true,
                                closeDelay: 500,
                                cssClass: "Popup-element-wrapper--lite",
                                cssClassContent: "Popup-content--lite",
                            }}
                        >
                            <span className="swWidget-dashboard-subTitle-text">
                                {widget.viewData.subtitleTextItem?.text}
                            </span>
                        </PopupHoverContainer>
                    </li>
                ) : null}

                {!!widget.apiParams.ExcludeTerms ||
                !!widget.apiParams.IncludeTerms ||
                !!widget.apiParams.ExcludeUrls ||
                !!widget.apiParams.IncludeUrls ? (
                    <div className="swWidget-dashboard-subTitle-boolean-search">
                        <BooleanSearchWidgetSubtitleItem
                            ExcludeTerms={widget.apiParams.ExcludeTerms}
                            IncludeTerms={widget.apiParams.IncludeTerms}
                            ExcludeUrls={widget.apiParams.ExcludeUrls}
                            IncludeUrls={widget.apiParams.IncludeUrls}
                        />
                    </div>
                ) : null}
            </ul>
        </div>
    );
};
